import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import SystemSettingsService from "@/Service/SystemSettingsService";
import { showAlert } from "./alertSlice";
import {
  UPLOADER_CHUNKED,
  UPLOADER_LEGACY,
  applyServerUploader,
  readCachedUploader,
} from "@/Service/UploaderToggle";
import {
  ANALYTICS_ON,
  ANALYTICS_OFF,
  applyServerAnalyticsEnabled,
  readCachedAnalyticsEnabled,
} from "@/Service/AnalyticsToggle";

// Seed Redux with the cached values so the very first render has something usable; the
// boot fetches refresh them before the admin clicks a control in the overwhelming
// majority of cases. `analyticsEnabled` is null when no admin has ever set it — the
// bootstrap layer then falls back to the env default (prod ON, dev OFF).
const initialState = {
  activeUploader: readCachedUploader() || UPLOADER_LEGACY,
  fetch: { status: "idle", error: null },
  update: { status: "idle", error: null },
  analyticsEnabled: readCachedAnalyticsEnabled(), // "on" | "off" | null
  analyticsFetch: { status: "idle", error: null },
  analyticsUpdate: { status: "idle", error: null },
};

export const fetchActiveUploader = createAsyncThunk(
  "systemSettings/fetchActiveUploader",
  async (_, { rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.getActiveUploader();
      return data?.value;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load uploader setting");
    }
  }
);

export const updateActiveUploader = createAsyncThunk(
  "systemSettings/updateActiveUploader",
  async (value, { dispatch, rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.updateActiveUploader(value);
      dispatch(showAlert("success", `Uploader switched to ${data?.value === UPLOADER_CHUNKED ? "chunked" : "legacy"} for all users.`));
      return data?.value;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        dispatch(showAlert("error", "You don't have permission to change the uploader."));
      } else {
        dispatch(showAlert("error", "Failed to switch uploader. Please retry."));
      }
      return rejectWithValue(error?.message || "Failed to update uploader setting");
    }
  }
);

export const fetchAnalyticsEnabled = createAsyncThunk(
  "systemSettings/fetchAnalyticsEnabled",
  async (_, { rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.getAnalyticsEnabled();
      // Backend returns null when the row doesn't exist — signals "use env default".
      return data?.value ?? null;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load analytics setting");
    }
  }
);

export const updateAnalyticsEnabled = createAsyncThunk(
  "systemSettings/updateAnalyticsEnabled",
  async (value, { dispatch, rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.updateAnalyticsEnabled(value);
      dispatch(showAlert(
        "success",
        `Analytics ${data?.value === ANALYTICS_ON ? "enabled" : "disabled"} for all users on this environment.`,
      ));
      return data?.value ?? null;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        dispatch(showAlert("error", "You don't have permission to change analytics."));
      } else {
        dispatch(showAlert("error", "Failed to change analytics. Please retry."));
      }
      return rejectWithValue(error?.message || "Failed to update analytics setting");
    }
  }
);

const systemSettingsSlice = createSlice({
  name: "systemSettings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveUploader.pending, (state) => {
        state.fetch.status = "loading";
        state.fetch.error = null;
      })
      .addCase(fetchActiveUploader.fulfilled, (state, action) => {
        state.fetch.status = "succeeded";
        if (action.payload === UPLOADER_CHUNKED || action.payload === UPLOADER_LEGACY) {
          state.activeUploader = action.payload;
          // Side-effect: keep the UploaderToggle module in sync (it's what
          // ChunkedUploadService / UploadService / the resume banner read synchronously).
          applyServerUploader(action.payload);
        }
      })
      .addCase(fetchActiveUploader.rejected, (state, action) => {
        state.fetch.status = "failed";
        state.fetch.error = action.payload || action.error.message;
      })
      .addCase(updateActiveUploader.pending, (state) => {
        state.update.status = "loading";
        state.update.error = null;
      })
      .addCase(updateActiveUploader.fulfilled, (state, action) => {
        state.update.status = "succeeded";
        if (action.payload === UPLOADER_CHUNKED || action.payload === UPLOADER_LEGACY) {
          state.activeUploader = action.payload;
          applyServerUploader(action.payload);
        }
      })
      .addCase(updateActiveUploader.rejected, (state, action) => {
        state.update.status = "failed";
        state.update.error = action.payload || action.error.message;
      })
      .addCase(fetchAnalyticsEnabled.pending, (state) => {
        state.analyticsFetch.status = "loading";
        state.analyticsFetch.error = null;
      })
      .addCase(fetchAnalyticsEnabled.fulfilled, (state, action) => {
        state.analyticsFetch.status = "succeeded";
        const value = action.payload;
        if (value === ANALYTICS_ON || value === ANALYTICS_OFF || value === null) {
          state.analyticsEnabled = value;
          applyServerAnalyticsEnabled(value);
        }
      })
      .addCase(fetchAnalyticsEnabled.rejected, (state, action) => {
        state.analyticsFetch.status = "failed";
        state.analyticsFetch.error = action.payload || action.error.message;
      })
      .addCase(updateAnalyticsEnabled.pending, (state) => {
        state.analyticsUpdate.status = "loading";
        state.analyticsUpdate.error = null;
      })
      .addCase(updateAnalyticsEnabled.fulfilled, (state, action) => {
        state.analyticsUpdate.status = "succeeded";
        const value = action.payload;
        if (value === ANALYTICS_ON || value === ANALYTICS_OFF || value === null) {
          state.analyticsEnabled = value;
          applyServerAnalyticsEnabled(value);
        }
      })
      .addCase(updateAnalyticsEnabled.rejected, (state, action) => {
        state.analyticsUpdate.status = "failed";
        state.analyticsUpdate.error = action.payload || action.error.message;
      });
  },
});

export const useSystemSettingsState = () =>
  useSelector((state) => state.systemSettings);

export const useSystemSettingsActions = () => {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      fetchActiveUploader: () => dispatch(fetchActiveUploader()),
      updateActiveUploader: (value) => dispatch(updateActiveUploader(value)),
      fetchAnalyticsEnabled: () => dispatch(fetchAnalyticsEnabled()),
      updateAnalyticsEnabled: (value) => dispatch(updateAnalyticsEnabled(value)),
    }),
    [dispatch]
  );
};

export default systemSettingsSlice.reducer;
